import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { UserRole } from '@worksy360/shared';

/** The demo tenant the seed script populates. Dev sign-in defaults here. */
export const DEMO_TENANT = 'tn_DEMO00000000000000000001';

export interface Session {
  tenantId: string;
  userId: string;
  userName: string;
  roles: UserRole[];
  token: string;
}

interface AuthState {
  session: Session | null;
  signIn: (input: { tenantId: string; userId: string; userName: string; roles: UserRole[] }) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);
const STORAGE_KEY = 'worksy360.session';

/** Base64url-encode a JSON object (browser). Mirrors the API's dev-token format. */
function base64url(obj: unknown): string {
  const json = JSON.stringify(obj);
  const b64 = btoa(String.fromCharCode(...new TextEncoder().encode(json)));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * DEV auth. Mints a `dev.<...>` token accepted by the API when AUTH_MODE=dev. In production this
 * provider is swapped for a Cognito-backed implementation; the rest of the app is unchanged.
 */
export function mintDevToken(input: { tenantId: string; userId: string; roles: UserRole[] }): string {
  return `dev.${base64url({ ...input, sub: `sub_${input.userId}` })}`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch {
      return null;
    }
  });

  const value = useMemo<AuthState>(() => ({
    session,
    signIn: ({ tenantId, userId, userName, roles }) => {
      const token = mintDevToken({ tenantId, userId, roles });
      const next: Session = { tenantId, userId, userName, roles, token };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setSession(next);
    },
    signOut: () => {
      localStorage.removeItem(STORAGE_KEY);
      setSession(null);
    },
  }), [session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
